/** Coarse request origin only; never an event, identity, or interaction identifier. */
export type ActivitySource = "user_initiated" | "foreground_automatic" | "background" | "unknown";

export type ActivityRequestInit = RequestInit & { activitySource?: ActivitySource };

const ACTIVITY_SOURCE_HEADER = "X-TB-Activity-Source";
const ACTIVITY_SOURCES = new Set<ActivitySource>([
  "user_initiated",
  "foreground_automatic",
  "background",
  "unknown",
]);

export function normalizeActivitySource(value: unknown): ActivitySource {
  return ACTIVITY_SOURCES.has(value as ActivitySource) ? (value as ActivitySource) : "unknown";
}

/** Use only at a known automatic trigger. Visibility never establishes user intent. */
export function getAutomaticActivitySource(): ActivitySource {
  if (typeof document === "undefined") return "unknown";
  if (document.visibilityState === "hidden") return "background";
  return document.visibilityState === "visible" ? "foreground_automatic" : "unknown";
}

function isFirstPartyApiRequest(path: string): boolean {
  try {
    const origin = typeof location !== "undefined" ? location.origin : undefined;
    // Root-relative API paths are unambiguously first party, including in SSR tests.
    const base = origin && origin !== "null" ? origin : "https://first-party.invalid";
    if ((!origin || origin === "null") && !path.startsWith("/api/")) return false;
    const target = new URL(path, base);
    return target.origin === base && target.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

/** Return a fresh init so overlapping requests and retries cannot share mutable origin state. */
export function withActivitySource(path: string, init: ActivityRequestInit = {}): RequestInit {
  const { activitySource, ...requestInit } = init;
  const headers = new Headers(requestInit.headers);
  headers.delete(ACTIVITY_SOURCE_HEADER);
  if (isFirstPartyApiRequest(path)) {
    headers.set(ACTIVITY_SOURCE_HEADER, normalizeActivitySource(activitySource));
  }
  return { ...requestInit, headers };
}
